/**
 * SSE client for the AI Chat Panel (M22).
 *
 * `POST /api/chat/messages` returns a Server-Sent Events stream.
 * `EventSource` only does GET, so this uses `fetch()` + a streaming
 * body reader and parses the `data: {json}\n\n` frames itself.
 *
 * The event protocol mirrors `ai_service/chat/streaming.py`:
 *   session | text | tool_call | tool_result | tool_error |
 *   confirmation | notice | error | done
 * The stream always ends with an explicit `done` — never infer
 * completion from silence.
 */

import { apiBaseUrl, authHeaders } from "./api";
import type { RichChatPageContext } from "./chat-context-rich";

/** A mutation tool's before/after preview — what the confirmation
 * card diffs. Mirrors `ConfirmationPreview` in ai_service. */
export interface ConfirmationPreview {
  action: string;
  resource: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  reversible: boolean;
  note: string;
}

export type ChatSSEEvent =
  | { type: "session"; session_id: string }
  | { type: "text"; text: string }
  | { type: "tool_call"; name: string }
  | {
      type: "tool_result";
      name: string;
      render: string;
      summary: string;
      data: Record<string, unknown>;
    }
  | { type: "tool_error"; name: string; message: string }
  | {
      type: "confirmation";
      confirmation_id: string;
      tool_name: string;
      preview: ConfirmationPreview;
    }
  | { type: "notice"; message: string }
  | { type: "error"; message: string }
  | {
      type: "done";
      session_id: string;
      cost_usd: string;
      tools_called: string[];
    };

export interface ChatHistoryTurn {
  role: "user" | "assistant";
  content: string;
}

export interface StreamChatBody {
  message: string;
  history: ChatHistoryTurn[];
  locale: string;
  current_url?: string | null;
  current_entity_id?: string | null;
  /** M25.1 — rich page context (filters / selection / focused
   * element / last action). */
  page_context?: RichChatPageContext | null;
  session_id?: string | null;
  legal_entity_id?: string | null;
}

/**
 * Stream one chat turn. `onEvent` fires for each parsed SSE event.
 * Resolves when the stream closes; rejects only on a transport-level
 * failure (the server's own errors arrive as `error` events).
 */
export async function streamChat(
  body: StreamChatBody,
  onEvent: (event: ChatSSEEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl}/api/chat/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders()),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return;
    onEvent({
      type: "error",
      message: "Couldn't reach the chat service.",
    });
    return;
  }

  if (!res.ok || !res.body) {
    onEvent({
      type: "error",
      message: `Chat request failed (${res.status}).`,
    });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            onEvent(JSON.parse(line.slice(6)) as ChatSSEEvent);
          } catch {
            // Malformed frame — skip it; the stream continues.
          }
        }
      }
    }
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return;
    onEvent({
      type: "error",
      message: "The chat connection dropped.",
    });
  }
}
